import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
    Card,
    CardContent,
    Stack,
    Button,
    Typography,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    IconButton,
    CircularProgress,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    LabelList,
    ReferenceLine,
} from 'recharts';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useHistory } from 'react-router-dom';
import { useTheme } from '@mui/styles';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { useCommonTranslation } from 'c-translation';
import type { CampaignPerformanceMapLiveDashboard } from 'c-sdk/Modules/Reports';
import type { LineItemUnderInvestigation, TalonOrderlineError } from 'c-sdk';
import {
    LineItemsUnderInvestigationButton,
    OrderlineErrorsButton,
} from 'c-main/Components/Campaign/CampaignReportingWrapper/TalonIssueButtons';

type CampaignStatusInfo = {
    chartStatusMessage: string | null;
    primaryStatusMessage: string | null;
    lineItemsUnderInvestigation: LineItemUnderInvestigation[];
    orderlineErrors: TalonOrderlineError[];
};

const SecondTab = ({
    data,
    isLoading,
}: {
    isLoading: boolean;
    data: CampaignPerformanceMapLiveDashboard;
}) => {
    const [analysisType, setAnalysisType] = useState('impact');
    const [filterType, setFilterType] = useState('all');
    const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
    const tableRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const history = useHistory();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    // table/tooltip colours, kept as before in light mode
    const tableColors = useMemo(
        () => ({
            headerBg: isDarkMode ? theme.palette.grey[800] : theme.palette.secondary.main,
            headerText: isDarkMode
                ? theme.palette.text.primary
                : theme.palette.secondary.contrastText,
            headerBorder: isDarkMode ? theme.palette.divider : theme.palette.secondary.contrastText,
            rowBg: isDarkMode ? theme.palette.grey[900] : theme.palette.grey[200],
            tooltipBg: isDarkMode ? theme.palette.background.paper : 'white',
            tooltipBorder: isDarkMode ? theme.palette.divider : '#ccc',
            good: isDarkMode ? theme.palette.success.main : 'green',
            bad: isDarkMode ? theme.palette.error.main : 'red',
        }),
        [isDarkMode, theme],
    );
    const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());

    const dataArray = useMemo(() => Object.values(data), [data]);
    const t = useCommonTranslation();

    const getCampaignStatusInfo = (campaign: any): CampaignStatusInfo => {
        let primaryStatusMessage: string | null = null;

        if (campaign.booking_summary_validated === false) {
            primaryStatusMessage = `${t(
                'Modules.Main.Campaigns.DataStatus.bookingPlanValidatedLabelAlert',
            )} data@uniledsoftware.com`;
        } else if (campaign?.notices?.overall?.awaiting_data) {
            primaryStatusMessage = t('Modules.Main.Campaigns.DataStatus.awaitingDataLabelAlert');
        } else if (campaign?.notices?.overall?.inspecting_data) {
            primaryStatusMessage = t('Modules.Main.Campaigns.DataStatus.inspectingDataLabelAlert');
        }

        // The same two issues a Talon campaign's reporting page flags, worded as its tooltips are.
        const lineItemsUnderInvestigation: LineItemUnderInvestigation[] =
            campaign?.line_items_under_investigation ?? [];
        const orderlineErrors: TalonOrderlineError[] = campaign?.orderline_errors ?? [];

        const chartStatusMessage = [
            primaryStatusMessage,
            lineItemsUnderInvestigation.length > 0 &&
                t('Modules.Main.Campaigns.Overview.Reports.lineItemsUnderInvestigation'),
            orderlineErrors.length > 0 &&
                t('Modules.Main.Campaigns.Overview.Reports.orderLineErrors'),
        ]
            .filter(Boolean)
            .join('\n');

        return {
            chartStatusMessage: chartStatusMessage || null,
            primaryStatusMessage,
            lineItemsUnderInvestigation,
            orderlineErrors,
        };
    };

    const chartData = useMemo(
        () =>
            dataArray
                .map(campaign => {
                    const performance =
                        analysisType === 'impact'
                            ? campaign.audience_performance
                            : campaign.plays_performance;

                    const trueValue = performance / 100;
                    const statusInfo = getCampaignStatusInfo(campaign);

                    return {
                        id: campaign.id.toString(),
                        name: campaign.name,
                        value: Math.min(trueValue, 5),
                        trueValue,
                        statusMessage: statusInfo.chartStatusMessage,
                        daysLeft: campaign.days_left,
                    };
                })
                .filter(c => {
                    if (filterType === 'above175') return c.trueValue > 1.75;
                    if (filterType === 'below100') return c.trueValue < 1.0;
                    return true;
                }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dataArray, analysisType, filterType, t],
    );

    const groupedData = useMemo(() => {
        const grouped: Record<
            string,
            {
                campaignName: string;
                campaignId: string;
                daysLeft: number;
                reference: string;
                audience_performance: number;
                plays_performance: number;
                audience_received: number;
                mediaOwners: any[];
                primaryStatusMessage: string | null;
                lineItemsUnderInvestigation: LineItemUnderInvestigation[];
                orderlineErrors: TalonOrderlineError[];
            }
        > = {};

        dataArray.forEach(campaign => {
            const owners = Object.values(campaign.media_owners || {}).map(owner => ({
                Owner: owner.name,
                allImpacts: owner.audience_received,
                '%ImpactPerformance': owner.audience_performance / 100,
                '%PlayPerformance': owner.plays_performance / 100,
            }));

            const statusInfo = getCampaignStatusInfo(campaign);

            grouped[campaign.id] = {
                campaignName: campaign.name,
                campaignId: campaign.id.toString(),
                daysLeft: campaign.days_left,
                reference: campaign.reference,
                audience_performance: campaign.audience_performance / 100,
                plays_performance: campaign.plays_performance / 100,
                audience_received: campaign.audience_received,
                mediaOwners: owners,
                primaryStatusMessage: statusInfo.primaryStatusMessage,
                lineItemsUnderInvestigation: statusInfo.lineItemsUnderInvestigation,
                orderlineErrors: statusInfo.orderlineErrors,
            };
        });

        return grouped;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataArray, t]);

    useEffect(() => {
        if (selectedCampaignId && tableRefs.current[selectedCampaignId]) {
            tableRefs.current[selectedCampaignId]?.scrollIntoView({ behavior: 'smooth' });
            setExpandedCampaigns(new Set([selectedCampaignId]));
        }
        const timeout = setTimeout(() => {
            setSelectedCampaignId(null);
        }, 2000);

        return () => clearTimeout(timeout);
    }, [selectedCampaignId]);

    const toggleExpanded = (campaignId: string) => {
        setExpandedCampaigns(prev => {
            const newSet = new Set(prev);
            if (newSet.has(campaignId)) {
                newSet.delete(campaignId);
            } else {
                newSet.add(campaignId);
            }
            return newSet;
        });
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Stack gap={2}>
            <Stack direction="row" spacing={1}>
                <Button
                    variant={analysisType === 'impact' ? 'contained' : 'outlined'}
                    onClick={() => setAnalysisType('impact')}
                >
                    {t('Modules.Main.Dashboard.impactPerformance')}
                </Button>
                <Button
                    variant={analysisType === 'play' ? 'contained' : 'outlined'}
                    onClick={() => setAnalysisType('play')}
                >
                    {t('Modules.Main.Dashboard.playPerformance')}
                </Button>
                <Button
                    variant={filterType === 'all' ? 'contained' : 'outlined'}
                    onClick={() => setFilterType('all')}
                >
                    All
                </Button>
                <Button
                    variant={filterType === 'above175' ? 'contained' : 'outlined'}
                    onClick={() => setFilterType('above175')}
                >
                    Above 175%
                </Button>
                <Button
                    variant={filterType === 'below100' ? 'contained' : 'outlined'}
                    onClick={() => setFilterType('below100')}
                >
                    Below 100%
                </Button>
            </Stack>

            <Box>
                <Card>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <ResponsiveContainer width="100%" height={720}>
                            <BarChart
                                data={chartData}
                                margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="name"
                                    angle={-75}
                                    textAnchor="end"
                                    interval={0}
                                    height={288}
                                    tick={{ fontSize: 11 }}
                                />
                                <YAxis
                                    domain={[0, 5]}
                                    ticks={[0, 1, 2, 3, 4, 5]}
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={value =>
                                        `${value * 100}${value >= 5 ? '+ ' : ''}%`
                                    }
                                />
                                <RechartsTooltip
                                    content={({ active, payload }) => {
                                        if (!active || !payload || payload.length === 0)
                                            return null;

                                        const dataPoint = payload[0].payload;
                                        const performance = `${(dataPoint.trueValue * 100).toFixed(
                                            0,
                                        )}%`;
                                        const daysLeft = dataPoint.daysLeft;

                                        return (
                                            <div
                                                style={{
                                                    backgroundColor: tableColors.tooltipBg,
                                                    border: `1px solid ${tableColors.tooltipBorder}`,
                                                    padding: '10px',
                                                    borderRadius: '4px',
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                <strong>{dataPoint.name}</strong>
                                                <br />
                                                <span style={{ color: theme.palette.primary.main }}>
                                                    Performance: {performance}
                                                </span>
                                                <br />
                                                <span style={{ color: theme.palette.primary.main }}>
                                                    Days Left: {daysLeft}
                                                </span>
                                                {dataPoint.statusMessage && (
                                                    <>
                                                        <br />
                                                        <br />
                                                        <span
                                                            style={{
                                                                color: theme.palette.error.main,
                                                            }}
                                                        >
                                                            {dataPoint.statusMessage}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    }}
                                />

                                <Bar
                                    dataKey="value"
                                    fill={theme.palette.primary.main}
                                    onClick={data => setSelectedCampaignId(data.id)}
                                    radius={[8, 8, 0, 0]}
                                >
                                    <LabelList
                                        dataKey="value"
                                        content={({ x, y, width, index }) => {
                                            const fullData =
                                                typeof index === 'number'
                                                    ? chartData[index]
                                                    : undefined;
                                            const statusMessage = fullData?.statusMessage;
                                            if (!statusMessage) return null;

                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            const iconX = x + width / 2 - 10;
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            const iconY = Math.max(0, y - 20);

                                            return (
                                                <foreignObject
                                                    x={iconX}
                                                    y={iconY}
                                                    width={20}
                                                    height={20}
                                                >
                                                    <Tooltip title={statusMessage}>
                                                        <span
                                                            style={{
                                                                display: 'inline-block',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <WarningAmberIcon
                                                                fontSize="small"
                                                                sx={{ color: tableColors.bad }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </foreignObject>
                                            );
                                        }}
                                    />
                                </Bar>

                                {filterType === 'all' && (
                                    <ReferenceLine y={1} stroke={theme.palette.secondary.main} />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </Box>

            <Card>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow
                                    style={{
                                        backgroundColor: tableColors.headerBg,
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            border: '1px solid',
                                            borderColor: tableColors.headerBorder,
                                        }}
                                        align="center"
                                    />
                                    <TableCell
                                        align="center"
                                        sx={{
                                            color: tableColors.headerText,
                                            border: '1px solid',
                                            borderColor: tableColors.headerBorder,
                                        }}
                                    >
                                        Campaign ID
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            color: tableColors.headerText,
                                            border: '1px solid',
                                            borderColor: tableColors.headerBorder,
                                        }}
                                    >
                                        Campaign Name
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            color: tableColors.headerText,
                                            border: '1px solid',
                                            borderColor: tableColors.headerBorder,
                                        }}
                                    >
                                        Campaign Reference
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            color: tableColors.headerText,
                                            border: '1px solid',
                                            borderColor: tableColors.headerBorder,
                                        }}
                                    >
                                        {t('Modules.Main.Dashboard.owner')}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            color: tableColors.headerText,
                                            border: '1px solid',
                                            borderColor: tableColors.headerBorder,
                                        }}
                                    >
                                        Days Left
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            color: tableColors.headerText,
                                            border: '1px solid',
                                            borderColor: tableColors.headerBorder,
                                        }}
                                    >
                                        {t('Modules.Main.Dashboard.allImpacts')}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            color: tableColors.headerText,
                                            border: '1px solid',
                                            borderColor: tableColors.headerBorder,
                                        }}
                                    >
                                        {t('Modules.Main.Dashboard.percImpactPerformance')}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            color: tableColors.headerText,
                                            border: '1px solid',
                                            borderColor: tableColors.headerBorder,
                                        }}
                                    >
                                        % Plays Performance
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            color: tableColors.headerText,
                                            border: '1px solid',
                                            borderColor: tableColors.headerBorder,
                                        }}
                                    >
                                        Status
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(groupedData).map(([campaignID, campaign]) => {
                                    const isExpanded = expandedCampaigns.has(campaignID);
                                    return (
                                        <React.Fragment key={campaignID}>
                                            <TableRow
                                                style={{ backgroundColor: tableColors.rowBg }}
                                                ref={el => {
                                                    tableRefs.current[campaignID] = el;
                                                }}
                                                onClick={() =>
                                                    history.push(`/campaigns/${campaignID}`)
                                                }
                                            >
                                                <TableCell width="1%" align="center">
                                                    <IconButton
                                                        size="small"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            toggleExpanded(campaignID);
                                                        }}
                                                    >
                                                        {isExpanded ? (
                                                            <KeyboardArrowDown />
                                                        ) : (
                                                            <KeyboardArrowRight />
                                                        )}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell align="center">{campaignID}</TableCell>
                                                <TableCell align="center">
                                                    {campaign.campaignName}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {campaign.reference}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography fontWeight="bold">
                                                        {campaign.mediaOwners.length}{' '}
                                                        {campaign.mediaOwners.length === 1
                                                            ? t('Modules.Main.Dashboard.owner')
                                                            : t('Modules.Main.Dashboard.owners')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    {campaign.daysLeft}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {Math.round(
                                                        campaign.audience_received,
                                                    ).toLocaleString()}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color:
                                                            campaign.audience_performance >= 0.995
                                                                ? tableColors.good
                                                                : tableColors.bad,
                                                    }}
                                                >
                                                    {(campaign.audience_performance * 100).toFixed(
                                                        0,
                                                    )}
                                                    %
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color:
                                                            campaign.plays_performance >= 0.995
                                                                ? tableColors.good
                                                                : tableColors.bad,
                                                    }}
                                                >
                                                    {(campaign.plays_performance * 100).toFixed(0)}%
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Stack
                                                        direction="row"
                                                        spacing={0.5}
                                                        justifyContent="center"
                                                        alignItems="center"
                                                    >
                                                        {campaign.primaryStatusMessage && (
                                                            <Tooltip
                                                                title={
                                                                    campaign.primaryStatusMessage
                                                                }
                                                            >
                                                                <WarningAmberIcon
                                                                    fontSize="small"
                                                                    sx={{ color: tableColors.bad }}
                                                                />
                                                            </Tooltip>
                                                        )}

                                                        {/* Keeps clicks off the row's navigation, including
                                                            those inside the dialogs, which bubble through the
                                                            portal. */}
                                                        <Box
                                                            display="flex"
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            {campaign.lineItemsUnderInvestigation
                                                                .length > 0 && (
                                                                <LineItemsUnderInvestigationButton
                                                                    size="small"
                                                                    rows={
                                                                        campaign.lineItemsUnderInvestigation
                                                                    }
                                                                />
                                                            )}
                                                            {campaign.orderlineErrors.length >
                                                                0 && (
                                                                <OrderlineErrorsButton
                                                                    size="small"
                                                                    errors={
                                                                        campaign.orderlineErrors
                                                                    }
                                                                />
                                                            )}
                                                        </Box>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>

                                            {isExpanded &&
                                                campaign.mediaOwners.map((owner, idx) => (
                                                    <TableRow
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        key={idx}
                                                        onClick={() =>
                                                            history.push(`/campaigns/${campaignID}`)
                                                        }
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <TableCell align="center" />
                                                        <TableCell align="center" />
                                                        <TableCell align="center" />
                                                        <TableCell align="center" />
                                                        <TableCell align="center">
                                                            {owner.Owner}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {campaign.daysLeft}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {Math.round(
                                                                owner.allImpacts,
                                                            ).toLocaleString()}
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            sx={{
                                                                color:
                                                                    owner['%ImpactPerformance'] >=
                                                                    0.995
                                                                        ? tableColors.good
                                                                        : tableColors.bad,
                                                            }}
                                                        >
                                                            {(
                                                                owner['%ImpactPerformance'] * 100
                                                            ).toFixed(0)}
                                                            %
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            sx={{
                                                                color:
                                                                    owner['%PlayPerformance'] >=
                                                                    0.995
                                                                        ? tableColors.good
                                                                        : tableColors.bad,
                                                            }}
                                                        >
                                                            {(
                                                                owner['%PlayPerformance'] * 100
                                                            ).toFixed(0)}
                                                            %
                                                        </TableCell>
                                                        <TableCell align="center" />
                                                    </TableRow>
                                                ))}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Stack>
    );
};

export default SecondTab;
